/**
 * UI-5a Task 4 — RecordingDetailDrawer.
 *
 * Slide-in drawer showing a single recording's full detail:
 *   - the FULL <RecordingPlayer> (HTML5 audio + PCI skip),
 *   - a metadata grid (date / duration / size / agent / queue / KEK / transcript),
 *   - a PCI pause-segments timeline (simple list of "Paused m:ss–m:ss"),
 *   - a transcript section, shown only when `recording.hasTranscript` is true,
 *   - a Delete action, gated by the `recordings:delete` permission.
 *
 * This is a detail VIEW, not a form — it does not use ResourceDrawer (which
 * mandates a Save button) nor ZodForm. The drawer shell here mirrors
 * ResourceDrawer's accessibility behaviour (backdrop click + Escape to close).
 */
import type { JSX } from 'react'
import { useEffect, useRef } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { RecordingPlayer } from '../../../modules/recordings/RecordingPlayer.js'
import type { Recording } from '../../../hooks/useRecordingsList.js'
import { useAuthStore } from '../../../store/authStore.js'
import { useToast } from '../../../hooks/useToast.js'
import { api } from '../../../api/client.js'
import { formatDate } from '../../../utils/formatDate.js'
import { useFocusTrap } from '../../../hooks/useFocusTrap.js'

export interface RecordingDetailDrawerProps {
  open: boolean
  recording: Recording | null
  onClose: () => void
  onDeleted: () => void
}

/** Formats a whole-second duration as `m:ss` (or `h:mm:ss` for long calls). */
function formatMmSs(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '—'
  const sec = Math.floor(totalSec)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`
  return `${m}:${ss}`
}

/** Formats a byte count into a short human-readable size. */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unitIdx = 0
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024
    unitIdx += 1
  }
  const rounded = value >= 10 || Number.isInteger(value)
    ? Math.round(value)
    : Math.round(value * 10) / 10
  return `${rounded} ${units[unitIdx]}`
}

function MetaRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </>
  )
}

export function RecordingDetailDrawer({
  open,
  recording,
  onClose,
  onDeleted,
}: RecordingDetailDrawerProps): JSX.Element {
  const toast = useToast()
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const canDelete = permissions.includes('recordings:delete')

  // Focus trap — keeps Tab focus inside the panel while the drawer is open.
  const trapRef = useFocusTrap<HTMLElement>(open)
  const previousFocus = useRef<HTMLElement | null>(null)

  // Capture active element when opening; restore it when closing.
  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  // Move initial focus to the first focusable element inside the panel on open.
  useEffect(() => {
    if (!open) return
    const first = trapRef.current?.querySelector<HTMLElement>(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    )
    first?.focus()
  }, [open, trapRef])

  // Close on Escape while the drawer is open.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open || !recording) {
    return <></>
  }

  function handleDelete(): void {
    // recording is guaranteed non-null here — the component early-returns
    // `<></>` above when `!recording`, so this branch is only reachable with
    // a valid recording. The non-null assertion satisfies TypeScript.
    const rec = recording!
    confirmDialog({
      message: `Permanently delete recording from ${formatDate(rec.createdAt)}? This cannot be undone.`,
      header: 'Delete Recording',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // The whole body is try/catch-wrapped so a rejecting `api.delete` can
      // never escape as an unhandled rejection — failures surface as a toast
      // and onDeleted/onClose are NOT called.
      accept: async () => {
        try {
          await api.delete(`/recordings/${rec.id}`)
          onDeleted()
          onClose()
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : 'Failed to delete recording',
          )
        }
      },
    })
  }

  const pauseSegments = recording.pciPauseSegments ?? []

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Drawer panel — focus trap applied here */}
      <aside
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label="Recording detail"
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            Recording Detail
          </h2>
          <button
            type="button"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
          {/* Full audio player */}
          <RecordingPlayer recordingId={recording.id} />

          {/* Metadata grid */}
          <section aria-label="Recording metadata">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
              <MetaRow label="Date" value={formatDate(recording.createdAt)} />
              <MetaRow
                label="Duration"
                value={formatMmSs(recording.durationSec)}
              />
              <MetaRow label="Size" value={formatBytes(recording.sizeBytes)} />
              <MetaRow
                label="Agent"
                value={recording.agentName ?? recording.agentId ?? '—'}
              />
              <MetaRow
                label="Queue"
                value={recording.queueName ?? recording.queueId ?? '—'}
              />
              <MetaRow label="KEK Version" value={`v${recording.kekVersion}`} />
              <MetaRow
                label="Transcript"
                value={recording.hasTranscript ? 'Yes' : 'No'}
              />
            </dl>
          </section>

          {/* PCI pause-segments timeline */}
          <section aria-label="PCI pause segments">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              PCI Pause Segments
            </h3>
            {pauseSegments.length === 0 ? (
              <p className="text-sm text-gray-500">No pause segments.</p>
            ) : (
              <ul className="space-y-1" data-testid="pci-segment-list">
                {pauseSegments.map((seg, i) => (
                  <li
                    key={`${seg.startOffsetMs}-${seg.endOffsetMs}-${i}`}
                    className="text-sm text-gray-700"
                  >
                    Paused {formatMmSs(seg.startOffsetMs / 1000)}–
                    {formatMmSs(seg.endOffsetMs / 1000)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Transcript section — only when a transcript exists */}
          {recording.hasTranscript ? (
            <section aria-label="Transcript" data-testid="transcript-section">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                Transcript
              </h3>
              <p className="text-sm text-gray-500">
                A transcript is available for this recording.
              </p>
            </section>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 border-t border-gray-200 px-6 py-4">
          {canDelete ? (
            <Button
              label="Delete"
              icon="pi pi-trash"
              severity="danger"
              outlined
              onClick={handleDelete}
            />
          ) : (
            <span />
          )}
          <Button label="Close" text severity="secondary" onClick={onClose} />
        </div>
      </aside>
    </>
  )
}
